/**
 * worleyTerrainPipeline — Voronoi-based mountain landscape.
 *
 * A height field generated by sampling the distance to the nearest
 * random point (Worley/Voronoi noise). Negative distance = valleys,
 * positive = mountains. We render it with simple Lambert + ambient
 * lighting, and add a sky gradient based on view direction.
 *
 * The camera looks down at the terrain from a fixed angle, slowly
 * panning across. Audio drives the terrain amplitude and the
 * camera pan speed.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface WorleyTerrainPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

// Worley noise: returns the distance to the nearest random point.
float worley(vec2 p, float jitter) {
  vec2 ip = floor(p);
  vec2 fp = fract(p);
  float minDist = 1.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 offset = vec2(float(i), float(j));
      vec2 sitePos = offset + hash22(ip + offset) * jitter;
      float d = length(fp - sitePos);
      minDist = min(minDist, d);
    }
  }
  return minDist;
}

float terrainHeight(vec2 p) {
  float t = uTime;
  // Combine Worley noise layers for natural-looking terrain.
  float w1 = worley(p * 1.5 + vec2(t * 0.1, 0.0), 0.9);
  float w2 = worley(p * 3.0 + vec2(0.0, t * 0.15), 0.7);
  // Audio: increase amplitude with bass.
  float amp = 0.5 + bass * 0.5;
  return (1.0 - w1) * amp + (1.0 - w2) * amp * 0.3;
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Screen coords (perspective projection onto the ground plane).
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Camera position (orbiting).
  float camAngle = uTime * 0.1 + bass * 0.3;
  vec3 ro = vec3(2.0 * cos(camAngle), 1.0, 2.0 * sin(camAngle));
  // Ground coords: project screen ray onto y=0 plane.
  // For simplicity, we use the screen X directly as world X and
  // a perspective scale based on Y.
  vec2 worldP = p * vec2(2.0, 1.0) + vec2(camAngle * 0.3, 0.0);
  // Sample terrain height.
  float h = terrainHeight(worldP);
  // Compute normal via finite differences.
  float eps = 0.01;
  float hx = terrainHeight(worldP + vec2(eps, 0.0));
  float hy = terrainHeight(worldP + vec2(0.0, eps));
  vec3 n = normalize(vec3(h - hx, eps, h - hy));
  // Lighting (sun above).
  vec3 lightDir = normalize(vec3(0.5, 1.0, 0.3));
  float diff = max(dot(n, lightDir), 0.0);
  // Color: green/brown gradient based on height.
  vec3 lowColor = vec3(0.20, 0.30, 0.10);  // valley: dark green
  vec3 highColor = vec3(0.85, 0.80, 0.65);  // peak: tan/snow
  vec3 col = mix(lowColor, highColor, smoothstep(0.0, 0.6, h));
  // Lighting + ambient.
  col = col * (0.3 + 0.7 * diff);
  // Audio: brighten peaks with high.
  col += vec3(1.0, 0.9, 0.7) * smoothstep(0.5, 0.8, h) * high * 0.4;
  // Sky in the upper part (background).
  vec3 sky = vec3(0.20, 0.30, 0.50);
  // Blend with sky based on screen Y position (top of screen = sky).
  float skyMask = smoothstep(0.0, 0.5, p.y);
  col = mix(col, sky, skyMask);

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeWorleyTerrainBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeWorleyTerrainRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createWorleyTerrainPipeline(gl: WebGL2RenderingContext): WorleyTerrainPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawWorleyTerrain(
  gl: WebGL2RenderingContext,
  pipe: WorleyTerrainPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
